"use client";

import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import { FakeDashboard } from "../mock/fake-dashboard";
import { FakeFunnelOutput } from "../mock/fake-funnel-output";

import { HeroCopy } from "./hero-copy";

type Tab = "build" | "convert";

const TABS: { id: Tab; label: string; index: string }[] = [
  { id: "build", label: "Build", index: "01" },
  { id: "convert", label: "Convert", index: "02" },
];

export function HeroV3Tabbed() {
  const [tab, setTab] = useState<Tab>("build");
  const [interacted, setInteracted] = useState(false);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    if (interacted) return;

    intervalRef.current = window.setInterval(() => {
      setTab((prev) => (prev === "build" ? "convert" : "build"));
    }, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [interacted]);

  function handleClick(next: Tab) {
    setInteracted(true);
    setTab(next);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }

  return (
    <section className="relative overflow-hidden bg-white">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[700px]"
        style={{
          background:
            "radial-gradient(800px 400px at 50% 10%, rgba(167, 139, 250, 0.18), transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-6xl px-6 pb-12 pt-14 sm:pt-20 lg:pt-24">
        <HeroCopy
          headline={
            <>
              Stop paying £97/month
              <br />
              for funnels that{" "}
              <span className="text-gradient-brand">flop.</span>
            </>
          }
          sub={
            <>
              Funnlr generates conversion-tuned funnel pages from 5 fields —
              ready to publish in seconds. Pay £67 once. Own them forever.
            </>
          }
        />

        <div className="mx-auto mt-12 max-w-5xl" data-reveal="pop">
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 shadow-sm">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => handleClick(t.id)}
                  className={cn(
                    "inline-flex h-9 items-center gap-2 rounded-full px-4 text-[12.5px] font-semibold transition-colors",
                    tab === t.id
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:text-slate-900",
                  )}
                >
                  <span
                    className={cn(
                      "text-[10px] font-bold tracking-[0.14em]",
                      tab === t.id ? "text-violet-300" : "text-slate-400",
                    )}
                  >
                    {t.index}
                  </span>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="relative mt-10 min-h-[480px] sm:min-h-[560px]">
            <div
              key={tab}
              className={cn(
                "fade-in",
                tab === "build" ? "block" : "hidden",
              )}
            >
              <FakeDashboard />
            </div>
            <div
              key={`${tab}-funnel`}
              className={cn(
                "fade-in flex justify-center pt-4",
                tab === "convert" ? "block" : "hidden",
              )}
            >
              <FakeFunnelOutput />
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-1.5">
            {TABS.map((t) => (
              <div
                key={t.id}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-500",
                  tab === t.id ? "w-6 bg-violet-600" : "w-1.5 bg-slate-300",
                )}
              />
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .fade-in {
          animation: fade-in 0.4s ease-out;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .fade-in {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}
